import { LIMITS } from '@shared/protocol.js';

const MAX_EDGE = 1800;

/**
 * Read an image file and shrink it until the encoded data URL fits inside the
 * op-size limit. Images travel to everyone else in the room, so a 12MP phone
 * photo has to be cut down before it goes anywhere.
 */
export async function downscaleImage(file: File): Promise<{ src: string; width: number; height: number }> {
  if (!file.type.startsWith('image/')) throw new Error('That file is not an image.');

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  let width = Math.max(1, Math.round(bitmap.width * scale));
  let height = Math.max(1, Math.round(bitmap.height * scale));

  // GIFs and PNGs with transparency have to stay PNG; everything else is
  // much smaller as a JPEG.
  const keepAlpha = file.type === 'image/png' || file.type === 'image/gif' || file.type === 'image/webp';
  let quality = 0.88;

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const src = draw(bitmap, width, height, keepAlpha && attempt < 2 ? 'image/png' : 'image/jpeg', quality);
    if (src.length <= LIMITS.maxImageBytes) {
      close(bitmap);
      return { src, width, height };
    }
    if (quality > 0.5) quality -= 0.15;
    else {
      width = Math.max(1, Math.round(width * 0.75));
      height = Math.max(1, Math.round(height * 0.75));
    }
  }

  close(bitmap);
  throw new Error('That image is too large to share, even after shrinking it.');
}

function draw(
  bitmap: ImageBitmap | HTMLImageElement,
  width: number,
  height: number,
  type: string,
  quality: number,
) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image.');
  ctx.imageSmoothingQuality = 'high';
  if (type === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas.toDataURL(type, quality);
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Safari can refuse some formats here; fall back to an <img>.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not read that image.'));
      img.src = url;
    });
  } finally {
    // The bitmap is already decoded into the element by the time we get here.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

function close(bitmap: ImageBitmap | HTMLImageElement) {
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
}
